import { BaseApiResponse } from "@interfaces/app.interface";
import { NotificationsInterfaceResponse } from "@interfaces/notifications.interface";
import { PropertyListResponse } from "@interfaces/properties.interface";
import { DashboardLayout } from "@layouts/index";
import {
  getAgentKycStatus,
  getListings,
  getPersonalKycStatus,
  getUserNotifications,
} from "@services/krent.service";
import { useEffect, useState } from "react";
import { useSessionStorage } from "../../hooks";
import { useNavigate } from "react-router-dom";
import KycAlert from "components/KycAlert";
import "./overview.css";

export const Overview = () => {
  const [user] = useSessionStorage("user", {});
  const [listings, setListings] = useState<PropertyListResponse[]>([]);

  const [renderKycAlert, setRenderKycAlert] = useState(false);
  const [kycStatus, setKycStatus] = useState("");
  const [notifications, setNotifications] = useState<
    NotificationsInterfaceResponse[]
  >([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchKyc = async () => {
      let render = false;
      const resp = await getPersonalKycStatus();

      resp === undefined && setKycStatus("undefined");

      if (resp?.status === "approved") {
        render = false;
      } else if (resp?.status === "rejected") {
        render = true;
        setKycStatus(`Your KYC was rejected: ${resp?.rejectionReason}`);
      } else if (resp?.status === "pending") {
        render = true;
        setKycStatus("Your KYC is pending approval");
      }

      if (user.role === "agent") {
        const agentResp = await getAgentKycStatus();

        if (agentResp?.status === "approved") {
          render = render || false;
        } else if (agentResp?.status === "rejected") {
          render = true;
          setKycStatus(`Your KYC was rejected: ${agentResp?.rejectionReason}`);
        } else if (agentResp?.status === "pending") {
          render = true;
          setKycStatus("Your KYC is pending approval");
        }
      }

      setRenderKycAlert(render);
    };

    fetchKyc();
  }, [user]);
  useEffect(() => {
    let abortController = new AbortController();

    getListings(user?._id, {}, abortController.signal)
      .then((res: any) => {
        setListings(res.data);
      })
      .catch((err) => {
        console.log(err);
      });

    return () => {
      if (!abortController.signal.aborted) abortController.abort();
    };
  }, [user]);

  useEffect(() => {
    let abortController = new AbortController();
    getUserNotifications(user?._id, abortController.signal)
      .then((res: BaseApiResponse<NotificationsInterfaceResponse[]>) => {
        setNotifications(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [user]);

  const getInitials = (): string => {
    if (user.firstname && user.lastname) {
      return `${user.firstname[0]}${user.lastname[0]}`.toUpperCase();
    }
    if (user.name) return user.name.slice(0, 2).toUpperCase();
    return "U";
  };

  const getDisplayName = (): string =>
    user.name ||
    user.prefferedName ||
    `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim() ||
    "User";

  const shortletCount = listings.filter((l) => l.intent === "shortlet").length;
  const rentalCount = listings.filter((l) => l.intent === "long lease").length;
  const salesCount = listings.filter((l) => l.intent === "sale").length;
  const publishedCount = listings.filter((l) => l.published === true).length;

  return (
    <DashboardLayout
      title="Overview"
      subtitle={`Welcome back, ${
        user?.name ||
        user?.prefferedName ||
        `${user?.firstname} ${user?.lastname}`
      }`}
    >
      <div className="container-fluid py-4 px-3 px-md-4">
        {/* KYC Alert */}
        {/* {kycStatus === "undefined" ? (
          <div className="mb-4">
            <div
              className="d-flex justify-content-between p-3"
              style={{
                backgroundColor: "#FFDFDC",
                borderLeft: "4px solid #FF0000",
              }}
            >
              <div
                className="d-flex gap-3 align-items-center"
                style={{ color: "#FF0000" }}
              >
                <i className="fa-regular fa-user fa-2xl"></i>
                <div>
                  <h6 className="m-0">Please complete your KYC.</h6>
                  <p className="text-light-black mb-0">
                    Complete your KYC Process to verify your identity
                  </p>
                </div>
              </div>
              <button
                className="bg-primary rounded-10 text-white fw-bold px-4"
                onClick={() => navigate("/settings?tab=kyc")}
              >
                Add KYC
              </button>
            </div>
          </div>
        ) : (
          <></>
        )} */}

        {renderKycAlert && <KycAlert status={kycStatus} />}
        {/* Profile Card */}
        <div className="profile-card d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <div className="d-flex align-items-center gap-3">
            {user.avatar ? (
              <div className="avatar">
                <img src={user.avatar} alt={getDisplayName()} />
              </div>
            ) : (
              <div className="avatar">{getInitials()}</div>
            )}
            <div>
              <div className="profile-name">
                {getDisplayName().toUpperCase()}
              </div>
              <div className="profile-email">{user.email ?? ""}</div>
            </div>
          </div>

          <button
            className="create-btn"
            onClick={() => navigate("/listings")}
            type="button"
          >
            <i className="fa-solid fa-plus-circle text-white fs-16"></i>
            Create Listing
          </button>
        </div>

        {/* Stats Grid */}
        <div className="row g-3 mb-4">
          {[
            { label: "Active Shortlet Listings", count: shortletCount },
            { label: "Active Rental Listings", count: rentalCount },
            { label: "Active Sales Listings", count: salesCount },
            { label: "Listings Published", count: publishedCount },
          ].map(({ label, count }) => (
            <div key={label} className="col-12 col-sm-6 col-md-3">
              <div className="stat-card">
                <div className="stat-label">{label}</div>
                <div className="stat-value">{count} Listings</div>
              </div>
            </div>
          ))}
        </div>

        {/* Notification Panel */}
        <div className="notif-panel">
          <div className="notif-header">
            <span className="notif-header-text">Notification Center</span>
          </div>

          <div className="notif-content">
            {notifications.length === 0 ? (
              <div className="text-center p-5">
                <p className="fw-semibold fs-6 mb-1">No notifications yet</p>
                <p className="text-muted" style={{ fontSize: 13 }}>
                  Any notification you receive would show up here.
                </p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className="notif-item"
                  style={{
                    borderLeft: notif.read ? "none" : "3px solid #14b8a6",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div className="notif-title">{notif.title}</div>
                    <div className="notif-desc">{notif.message}</div>
                    <div className="notif-time">
                      {new Date(notif.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (notif.type === "wallet") {
                        navigate(user.role === "user" ? "/caution-fee" : "/wallet");
                      } else if (notif.type === "property") {
                        navigate("/listings");
                      } else if (notif.type === "transaction") {
                        if (user.role === "user") {
                          navigate("/my-bookings");
                        } else {
                          navigate("/requests");
                        }
                      } else {
                        if (user.role === "user") {
                          navigate("/my-bookings");
                        } else {
                          navigate("/requests");
                        }
                      }
                    }}
                    className="view-link"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    View here
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
